import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { ChatContextType, ChatMessage } from './chat';
import { AuthService } from '../auth/auth';

export interface ChatMessageEvent {
  type: 'CREATED' | 'UPDATED' | 'DELETED';
  message: ChatMessage;
}

/**
 * Минимальный STOMP-клиент поверх нативного WebSocket (без sockjs) — тот же паттерн,
 * что InvoiceSocketService/DealReportSocketService. Для DEAL подписывается на
 * /topic/chat/deal/{contextId}. Для REQUEST — на /topic/chat/request/{contextId}/forwarder/{forwarderId}
 * (приватный тред владелец↔конкретный экспедитор), см. ChatService.java.
 */
@Injectable({ providedIn: 'root' })
export class ChatSocketService {
  constructor(private zone: NgZone, private authService: AuthService) {}

  /**
   * Для REQUEST forwarderId обязателен (какой тред слушаем). asBroker=true подписывается
   * на /topic/.../broker (реальные имена) — см. ChatService.broadcast: чат заявки шлёт два
   * отдельных топика, т.к. один STOMP-топик не может по-разному анонимизировать payload
   * для разных подписчиков.
   */
  watch(contextType: ChatContextType, contextId: number, forwarderId?: number, asBroker = false): Observable<ChatMessageEvent> {
    return new Observable(subscriber => {
      if (typeof window === 'undefined' || typeof WebSocket === 'undefined') {
        subscriber.complete();
        return;
      }

      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/cargo/ws`;
      let socket: WebSocket | null = null;
      let closed = false;
      const subId = 'sub-chat';

      const sendFrame = (command: string, headers: Record<string, string> = {}, body = '') => {
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        const lines = [command, ...Object.entries(headers).map(([k, v]) => `${k}:${v}`), '', body];
        socket.send(lines.join('\n') + '\0');
      };

      try {
        socket = new WebSocket(url);
      } catch (e) {
        subscriber.error(e);
        return;
      }

      socket.onopen = () => {
        const token = this.authService.getToken();
        const connectHeaders: Record<string, string> = { 'accept-version': '1.2,1.1,1.0', 'heart-beat': '0,0' };
        if (token) connectHeaders['Authorization'] = `Bearer ${token}`;
        sendFrame('CONNECT', connectHeaders);
      };

      socket.onmessage = (event) => {
        const frames = String(event.data).split('\0').filter(Boolean);
        for (const raw of frames) {
          const parsed = parseStomp(raw);
          if (!parsed) continue;
          if (parsed.command === 'CONNECTED') {
            const suffix = asBroker ? '/broker' : '';
            const destination = contextType === 'REQUEST'
              ? `/topic/chat/request/${contextId}/forwarder/${forwarderId}${suffix}`
              : `/topic/chat/deal/${contextId}${suffix}`;
            sendFrame('SUBSCRIBE', { id: subId, destination });
          } else if (parsed.command === 'MESSAGE' && parsed.body) {
            try {
              const payload = JSON.parse(parsed.body) as ChatMessageEvent;
              this.zone.run(() => subscriber.next(payload));
            } catch {
              // ignore malformed frames
            }
          } else if (parsed.command === 'ERROR') {
            this.zone.run(() => subscriber.error(new Error(parsed.body || 'STOMP error')));
          }
        }
      };

      socket.onerror = () => {
        this.zone.run(() => subscriber.error(new Error('WebSocket error')));
      };

      socket.onclose = () => {
        if (!closed) {
          this.zone.run(() => subscriber.complete());
        }
      };

      return () => {
        closed = true;
        try {
          sendFrame('UNSUBSCRIBE', { id: subId });
          sendFrame('DISCONNECT');
        } catch {
          // ignore
        }
        socket?.close();
      };
    });
  }
}

function parseStomp(raw: string): { command: string; body: string } | null {
  const trimmed = raw.replace(/^\n+/, '');
  if (!trimmed) return null;
  const headerEnd = trimmed.indexOf('\n\n');
  const headerPart = headerEnd >= 0 ? trimmed.slice(0, headerEnd) : trimmed;
  const body = headerEnd >= 0 ? trimmed.slice(headerEnd + 2) : '';
  const command = headerPart.split('\n')[0]?.trim();
  if (!command) return null;
  return { command, body };
}
